import React, { useState, useEffect } from 'react';
import {
    Box,
    Flex,
    Heading,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Badge,
    Button,
    IconButton,
    useToast,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    useDisclosure,
    Text,
    VStack,
    HStack,
    SimpleGrid,
    Select,
    Link,
    Icon,
    Input,
    InputGroup,
    InputLeftElement,
    Card,
    CardBody,
    Divider,
    Spinner,
    Textarea,
    List,
    ListItem
} from '@chakra-ui/react';
import {
    ViewIcon,
    DeleteIcon,
    SearchIcon,
    DownloadIcon,
    CheckCircleIcon,
    InfoIcon,
    AttachmentIcon,
    ExternalLinkIcon
} from '@chakra-ui/icons';
import axiosInstance from '../services/axiosInstance';
import LayoutDashboard from '../shared/LayoutDashboard';
import { urlvideo } from '../services/urls';

const AdminRecruitmentManagement = () => {
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedApp, setSelectedApp] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [adminNotes, setAdminNotes] = useState('');

    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();

    const primaryColor = '#F2C919';
    const secondaryColor = '#2C3E50';

    useEffect(() => {
        fetchApplications();
    }, [statusFilter]);

    const fetchApplications = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get('/recruitment', {
                params: { status: statusFilter }
            });
            setApplications(response.data.applications);
        } catch (error) {
            toast({
                title: "خطأ",
                description: "تعذر جلب البيانات",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetails = (app) => {
        setSelectedApp(app);
        setAdminNotes(app.adminNotes || '');
        onOpen();
    };

    const handleUpdateStatus = async (newStatus) => {
        try {
            setIsUpdatingStatus(true);
            const response = await axiosInstance.patch(`/recruitment/${selectedApp._id}/status`, {
                status: newStatus,
                adminNotes
            });

            if (response.data.success) {
                toast({
                    title: "تم التحديث",
                    description: "تم تغيير حالة الطلب بنجاح",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
                fetchApplications();
                onClose();
            }
        } catch (error) {
            toast({
                title: "خطأ",
                description: "تعذر تحديث الحالة",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("هل أنت متأكد من حذف هذا الطلب؟")) return;

        try {
            await axiosInstance.delete(`/recruitment/${id}`);
            toast({
                title: "تم الحذف",
                description: "تم حذف الطلب بنجاح",
                status: "success",
            });
            fetchApplications();
        } catch (error) {
            toast({
                title: "خطأ",
                description: "تعذر حذف الطلب",
                status: "error",
            });
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'pending': return <Badge colorScheme="orange">قيد الانتظار</Badge>;
            case 'reviewed': return <Badge colorScheme="blue">تمت المراجعة</Badge>;
            case 'accepted': return <Badge colorScheme="green">مقبول</Badge>;
            case 'rejected': return <Badge colorScheme="red">مرفوض</Badge>;
            default: return <Badge>{status}</Badge>;
        }
    };

    const filteredApps = applications.filter(app =>
        app.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.phone.includes(searchTerm) ||
        (Array.isArray(app.subjects) ? app.subjects.join(' ').toLowerCase() : app.subjects.toLowerCase()).includes(searchTerm.toLowerCase())
    );

    return (
        <Box p={6} dir="rtl">
            <VStack align="stretch" spacing={6}>
                <Flex justify="space-between" align="center">
                    <Heading size="lg" color={secondaryColor} fontFamily="Cairo, sans-serif">إدارة طلبات الانتداب</Heading>
                    <HStack spacing={4}>
                        <Select
                            placeholder="تصفية حسب الحالة"
                            maxW="200px"
                            bg="white"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="pending">قيد الانتظار</option>
                            <option value="reviewed">تمت المراجعة</option>
                            <option value="accepted">مقبول</option>
                            <option value="rejected">مرفوض</option>
                        </Select>
                        <InputGroup maxW="300px">
                            <InputLeftElement pointerEvents="none">
                                <SearchIcon color="gray.400" />
                            </InputLeftElement>
                            <Input
                                placeholder="بحث بالاسم أو الهاتف أو المادة..."
                                bg="white"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </InputGroup>
                    </HStack>
                </Flex>

                <Card bg="white" borderRadius="xl" boxShadow="sm">
                    <CardBody p={0}>
                        {loading ? (
                            <Flex p={20} justify="center"><Spinner size="xl" color={primaryColor} /></Flex>
                        ) : (
                            <Table variant="simple" size="md">
                                <Thead bg="gray.50">
                                    <Tr>
                                        <Th textAlign="right">الاسم</Th>
                                        <Th textAlign="right">الهاتف</Th>
                                        <Th textAlign="right">الولاية</Th>
                                        <Th textAlign="right">المادة</Th>
                                        <Th textAlign="right">الحالة</Th>
                                        <Th textAlign="center">الإجراءات</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {filteredApps.length === 0 ? (
                                        <Tr><Td colSpan={6} textAlign="center" py={10}>لا توجد طلبات مطابقة</Td></Tr>
                                    ) : (
                                        filteredApps.map((app) => (
                                            <Tr key={app._id} _hover={{ bg: 'gray.50' }}>
                                                <Td fontWeight="bold">{app.fullName}</Td>
                                                <Td>{app.phone}</Td>
                                                <Td>{app.state}</Td>
                                                <Td>{Array.isArray(app.subjects) ? app.subjects.join(', ') : app.subjects}</Td>
                                                <Td>{getStatusBadge(app.status)}</Td>
                                                <Td textAlign="center">
                                                    <HStack spacing={2} justify="center">
                                                        <IconButton
                                                            icon={<ViewIcon />}
                                                            colorScheme="blue"
                                                            variant="ghost"
                                                            onClick={() => handleViewDetails(app)}
                                                            aria-label="عرض التفاصيل"
                                                        />
                                                        <IconButton
                                                            icon={<DeleteIcon />}
                                                            colorScheme="red"
                                                            variant="ghost"
                                                            onClick={() => handleDelete(app._id)}
                                                            aria-label="حذف"
                                                        />
                                                    </HStack>
                                                </Td>
                                            </Tr>
                                        ))
                                    )}
                                </Tbody>
                            </Table>
                        )}
                    </CardBody>
                </Card>
            </VStack>

            {/* Details Modal */}
            <Modal isOpen={isOpen} onClose={onClose} size="3xl">
                <ModalOverlay />
                <ModalContent dir="rtl">
                    <ModalHeader borderBottom="1px solid" borderColor="gray.100">تفاصيل طلب الانتداب</ModalHeader>
                    <ModalCloseButton left="8px" right="auto" />
                    <ModalBody py={6}>
                        {selectedApp && (
                            <VStack align="stretch" spacing={6}>
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                                    <Box>
                                        <Text fontWeight="bold" color="gray.500" fontSize="sm">الاسم واللقب:</Text>
                                        <Text fontSize="lg">{selectedApp.fullName}</Text>
                                    </Box>
                                    <Box>
                                        <Text fontWeight="bold" color="gray.500" fontSize="sm">رقم الهاتف:</Text>
                                        <Text fontSize="lg">{selectedApp.phone}</Text>
                                    </Box>
                                    <Box>
                                        <Text fontWeight="bold" color="gray.500" fontSize="sm">الولاية:</Text>
                                        <Text fontSize="lg">{selectedApp.state}</Text>
                                    </Box>
                                    <Box>
                                        <Text fontWeight="bold" color="gray.500" fontSize="sm">المستوى التعليمي:</Text>
                                        <Text fontSize="lg">{selectedApp.educationLevel}</Text>
                                    </Box>
                                    <Box>
                                        <Text fontWeight="bold" color="gray.500" fontSize="sm">المواد المدرسّة:</Text>
                                        <HStack wrap="wrap">
                                            {(Array.isArray(selectedApp.subjects) ? selectedApp.subjects : [selectedApp.subjects]).map((sub, i) => (
                                                <Badge key={i} colorScheme="purple">{sub}</Badge>
                                            ))}
                                        </HStack>
                                    </Box>
                                    <Box>
                                        <Text fontWeight="bold" color="gray.500" fontSize="sm">المستويات المدرسّة:</Text>
                                        <HStack wrap="wrap">
                                            {(Array.isArray(selectedApp.levels) ? selectedApp.levels : [selectedApp.levels]).map((lvl, i) => (
                                                <Badge key={i} colorScheme="cyan">{lvl}</Badge>
                                            ))}
                                        </HStack>
                                    </Box>
                                    {selectedApp.sections && selectedApp.sections.length > 0 && (
                                        <Box gridColumn="span 2">
                                            <Text fontWeight="bold" color="gray.500" fontSize="sm">الشعب حسب المستوى:</Text>
                                            <VStack align="stretch" spacing={2} mt={1}>
                                                {selectedApp.sections.map((item, idx) => (
                                                    <HStack key={idx} spacing={2}>
                                                        <Badge variant="outline" colorScheme="gray">
                                                            {item.level === 'BAC' ? 'باكالوريا' : item.level === '2' ? 'الثانية ثانوي' : item.level === '3' ? 'الثالثة ثانوي' : item.level}
                                                        </Badge>
                                                        <HStack wrap="wrap">
                                                            {(item.sections || []).map((sec, i) => (
                                                                <Badge key={i} colorScheme="orange" variant="subtle">{sec}</Badge>
                                                            ))}
                                                        </HStack>
                                                    </HStack>
                                                ))}
                                            </VStack>
                                        </Box>
                                    )}
                                </SimpleGrid>

                                <Box>
                                    <Text fontWeight="bold" color="gray.500" fontSize="sm" mb={2}>المؤسسات التعليمية:</Text>
                                    <List spacing={1} bg="gray.50" p={2} borderRadius="md" border="1px solid" borderColor="gray.100">
                                        {(Array.isArray(selectedApp.institution) ? selectedApp.institution : [selectedApp.institution]).map((inst, i) => (
                                            <ListItem key={i} fontSize="md">• {inst}</ListItem>
                                        ))}
                                    </List>
                                </Box>

                                <Divider />

                                <Box>
                                    <Text fontWeight="bold" color="gray.500" fontSize="sm" mb={1}>الخبرة المهنية:</Text>
                                    <Text bg="gray.50" p={4} borderRadius="lg">{selectedApp.experience}</Text>
                                </Box>

                                <HStack spacing={10}>
                                    <HStack>
                                        <Icon as={selectedApp.hasTablet ? CheckCircleIcon : InfoIcon} color={selectedApp.hasTablet ? "green.500" : "gray.300"} />
                                        <Text>لوحة رقمية (Tablet)</Text>
                                    </HStack>
                                    <HStack>
                                        <Icon as={selectedApp.hasPDF ? CheckCircleIcon : InfoIcon} color={selectedApp.hasPDF ? "green.500" : "gray.300"} />
                                        <Text>دروس PDF</Text>
                                    </HStack>
                                </HStack>

                                <Box>
                                    <Text fontWeight="bold" color="gray.500" fontSize="sm" mb={2}>الوثائق المرفقة:</Text>
                                    {selectedApp.educationDocuments && selectedApp.educationDocuments.length > 0 ? (
                                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                            {selectedApp.educationDocuments.map((doc, idx) => (
                                                <HStack key={idx} p={3} border="1px solid" borderColor="gray.100" borderRadius="md">
                                                    <AttachmentIcon color={primaryColor} />
                                                    <VStack align="start" spacing={0} flex={1}>
                                                        <Text fontSize="xs" isTruncated maxW="150px">{doc.originalFilename}</Text>
                                                        <Text fontSize="2xs" color="gray.400">{(doc.size / 1024 / 1024).toFixed(2)} MB</Text>
                                                    </VStack>
                                                    <IconButton
                                                        as="a"
                                                        href={`${urlvideo.endsWith('/') ? urlvideo.slice(0, -1) : urlvideo}/api/recruitment/download/${selectedApp._id}/${idx}`}
                                                        target="_blank"
                                                        icon={<DownloadIcon />}
                                                        size="sm"
                                                        variant="ghost"
                                                        aria-label="تحميل"
                                                    />
                                                    <IconButton
                                                        as="a"
                                                        href={`${urlvideo.endsWith('/') ? urlvideo.slice(0, -1) : urlvideo}/${doc.path.startsWith('/') ? doc.path.slice(1) : doc.path}`}
                                                        target="_blank"
                                                        icon={<ExternalLinkIcon />}
                                                        size="sm"
                                                        variant="ghost"
                                                        aria-label="عرض"
                                                    />
                                                </HStack>
                                            ))}
                                        </SimpleGrid>
                                    ) : (
                                        <Text color="gray.400" fontSize="sm">لا توجد وثائق مرفقة</Text>
                                    )}
                                </Box>

                                <Divider />

                                <Box>
                                    <Text fontWeight="bold" color="gray.500" fontSize="sm" mb={2}>ملاحظات الإدارة:</Text>
                                    <Textarea
                                        value={adminNotes}
                                        onChange={(e) => setAdminNotes(e.target.value)}
                                        placeholder="أضف ملاحظاتك هنا..."
                                        rows={2}
                                    />
                                </Box>
                            </VStack>
                        )}
                    </ModalBody>
                    <ModalFooter borderTop="1px solid" borderColor="gray.100" bg="gray.50">
                        <HStack spacing={3} w="100%" justify="center">
                            <Button colorScheme="green" onClick={() => handleUpdateStatus('accepted')} isLoading={isUpdatingStatus}>قبول</Button>
                            <Button colorScheme="blue" onClick={() => handleUpdateStatus('reviewed')} isLoading={isUpdatingStatus}>تمت المراجعة</Button>
                            <Button colorScheme="red" onClick={() => handleUpdateStatus('rejected')} isLoading={isUpdatingStatus}>رفض</Button>
                            <Button variant="ghost" onClick={onClose}>إغلاق</Button>
                        </HStack>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default AdminRecruitmentManagement;
